'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

export type TraceSourceFilter = 'all' | 'neo' | 'co-pilot';

export type TraceViewSettings = {
  showHttpViewGlobal: boolean;
  httpViewPerCard: Record<string, boolean>;
  showDetailedView: boolean;
  isPlanExpanded: boolean;
  showCondensedView: boolean;
  sourceFilter: TraceSourceFilter;
  showHiddenSteps: boolean;
};

type TraceViewSettingsContextType = {
  settings: TraceViewSettings;
  setShowHttpViewGlobal: (show: boolean) => void;
  setCardHttpView: (cardId: string, showHttp: boolean) => void;
  shouldShowHttpView: (cardId: string) => boolean;
  setShowDetailedView: (show: boolean) => void;
  setIsPlanExpanded: (expanded: boolean) => void;
  setShowCondensedView: (show: boolean) => void;
  setSourceFilter: (filter: TraceSourceFilter) => void;
  setShowHiddenSteps: (show: boolean) => void;
};

const defaultSettings: TraceViewSettings = {
  showHttpViewGlobal: false,
  httpViewPerCard: {},
  showDetailedView: true,
  isPlanExpanded: true,
  showCondensedView: false,
  sourceFilter: 'all',
  showHiddenSteps: false,
};

export const TraceViewSettingsContext = createContext<
  TraceViewSettingsContextType | undefined
>(undefined);

type TraceViewSettingsProviderProps = {
  children: ReactNode;
  initialSettings?: Partial<TraceViewSettings>;
  onSettingsChange?: (settings: TraceViewSettings) => void;
};

export function TraceViewSettingsProvider({
  children,
  initialSettings,
  onSettingsChange,
}: TraceViewSettingsProviderProps) {
  const [settings, setSettings] = useState<TraceViewSettings>(() => ({
    ...defaultSettings,
    ...initialSettings,
  }));

  const updateSettings = useCallback(
    (
      updater:
        | Partial<TraceViewSettings>
        | ((prev: TraceViewSettings) => Partial<TraceViewSettings>),
    ) => {
      setSettings((prev) => {
        const updates = typeof updater === 'function' ? updater(prev) : updater;
        const newSettings = { ...prev, ...updates };
        onSettingsChange?.(newSettings);
        return newSettings;
      });
    },
    [onSettingsChange],
  );

  const setShowHttpViewGlobal = useCallback(
    (show: boolean) => {
      updateSettings({
        showHttpViewGlobal: show,
        httpViewPerCard: {},
      });
    },
    [updateSettings],
  );

  const setCardHttpView = useCallback(
    (cardId: string, showHttp: boolean) => {
      updateSettings((prev) => ({
        httpViewPerCard: {
          ...prev.httpViewPerCard,
          [cardId]: showHttp,
        },
      }));
    },
    [updateSettings],
  );

  const shouldShowHttpView = useCallback(
    (cardId: string) => {
      const cardHttpViewOverride = settings.httpViewPerCard[cardId];
      if (cardHttpViewOverride !== undefined) {
        return cardHttpViewOverride;
      }
      return settings.showHttpViewGlobal;
    },
    [settings.httpViewPerCard, settings.showHttpViewGlobal],
  );

  const setShowDetailedView = useCallback(
    (show: boolean) => {
      updateSettings({ showDetailedView: show });
    },
    [updateSettings],
  );

  const setIsPlanExpanded = useCallback(
    (expanded: boolean) => {
      updateSettings({ isPlanExpanded: expanded });
    },
    [updateSettings],
  );

  const setShowCondensedView = useCallback(
    (show: boolean) => {
      updateSettings({ showCondensedView: show });
    },
    [updateSettings],
  );

  const setSourceFilter = useCallback(
    (filter: TraceSourceFilter) => {
      updateSettings({ sourceFilter: filter });
    },
    [updateSettings],
  );

  const setShowHiddenSteps = useCallback(
    (show: boolean) => {
      updateSettings({ showHiddenSteps: show });
    },
    [updateSettings],
  );

  const value = useMemo(
    () => ({
      settings,
      setShowHttpViewGlobal,
      setCardHttpView,
      shouldShowHttpView,
      setShowDetailedView,
      setIsPlanExpanded,
      setShowCondensedView,
      setSourceFilter,
      setShowHiddenSteps,
    }),
    [
      settings,
      setShowHttpViewGlobal,
      setCardHttpView,
      shouldShowHttpView,
      setShowDetailedView,
      setIsPlanExpanded,
      setShowCondensedView,
      setSourceFilter,
      setShowHiddenSteps,
    ],
  );

  return (
    <TraceViewSettingsContext.Provider value={value}>
      {children}
    </TraceViewSettingsContext.Provider>
  );
}

export function useTraceViewSettings(): TraceViewSettingsContextType {
  const context = useContext(TraceViewSettingsContext);
  if (!context) {
    return {
      settings: defaultSettings,
      setShowHttpViewGlobal: () => undefined,
      setCardHttpView: () => undefined,
      shouldShowHttpView: () => false,
      setShowDetailedView: () => undefined,
      setIsPlanExpanded: () => undefined,
      setShowCondensedView: () => undefined,
      setSourceFilter: () => undefined,
      setShowHiddenSteps: () => undefined,
    };
  }
  return context;
}

export function useTraceViewSettingsOptional():
  | TraceViewSettingsContextType
  | undefined {
  return useContext(TraceViewSettingsContext);
}
